import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { Player, BigPlayButton, ControlBar, Shortcut } from 'video-react'
import { OutboundLink } from 'gatsby-plugin-google-analytics'

class HeroVideo extends Component {
  constructor (props) {
    super(props)
    this.state = {
      isMobile: false,
      isIDevice: false,
      playOverlay: true
    }

    this.playFullScreen = this.playFullScreen.bind(this)
    this.closeFullScreen = this.closeFullScreen.bind(this)
    this.handleFullScreenState = this.handleFullScreenState.bind(this)
    this.handleInPlacePlayEnd = this.handleInPlacePlayEnd.bind(this)
    this.onMouseEnter = this.onMouseEnter.bind(this)
    this.onMouseLeave = this.onMouseLeave.bind(this)
    this.renderPlayerControl = this.renderPlayerControl.bind(this)
    this.getScalingCoordinates = this.getScalingCoordinates.bind(this)
  }

  componentDidMount () {
    const isMobile = window.innerWidth < 768 // iPad width
    const isIDevice = window.navigator.userAgent.match(/iPhone|iPad|iPod/i)
    const { fullscreen, fullscreenVideo, player } = this.refs

    this.setState({
      isMobile,
      isIDevice
    })

    if (fullscreen) {
      fullscreen.subscribeToStateChange(this.handleFullScreenState)
    }

    if (this.props.playInPlace) {
      player.subscribeToStateChange(this.handleInPlacePlayEnd)
    }

    if (isIDevice && fullscreenVideo) {
      fullscreenVideo.querySelector('video').addEventListener(
        'webkitendfullscreen', this.closeFullScreen)
    }
  }

  componentWillUnmount () {
    if (this.state.isIDevice && this.refs.fullscreenVideo) {
      this.refs.fullscreenVideo.querySelector('video').removeEventListener(
        'webkitendfullscreen', this.closeFullScreen)
    }
  }

  handleFullScreenState ({ ended, isFullscreen, paused, userActivity }) {
    if (ended && !isFullscreen) {
      this.closeFullScreen()

      if (this.state.isIDevice) {
        this.refs.fullscreenVideo.querySelector('video').webkitExitFullscreen()
      }
    }

    const { brandVideo, caseStudyVideo } = this.props

    if (brandVideo || caseStudyVideo) {
      const activeItem = document.querySelector(`${brandVideo ? '.active' : '.case-study--active'}`)
      if (activeItem && userActivity) {
        activeItem.querySelector('.modal--close').classList.remove('hide')
      } else if (activeItem && !paused) {
        activeItem.querySelector('.modal--close').classList.add('hide')
      }
    }
  }

  handleInPlacePlayEnd ({ ended }) {
    const { videoWrapper, mainVideo } = this.refs

    if (ended && !this.state.playOverlay) {
      this.setState({
        playOverlay: true
      })

      videoWrapper.style.zIndex = 'auto'
      mainVideo.classList.remove('hide-after')
    }
  }

  getScalingCoordinates () {
    const { videoWrapper } = this.refs
    const { brandVideo, caseStudyVideo } = this.props

    const videoWidth = videoWrapper.offsetWidth
    // Video height w/o the bottom border if not brand or case study video
    const videoHeight = brandVideo || caseStudyVideo
      ? videoWrapper.offsetHeight
      : videoWrapper.offsetHeight - 5
    // The amount needed to scale horizontally to match the screen width
    const scaleX = window.innerWidth / videoWidth
    // The amount needed to scale vertically to match the screen height
    const scaleY = window.innerHeight / videoHeight
    // Using `scale()` changes the size of the pixels so we need to calculate
    // the `offsetTop` in terms of `scaleY`.
    const topOffset = videoWrapper.offsetTop / scaleY
    const aspectRatio = videoHeight / videoWidth

    // The video needs to be scaled again to maintain the aspect ratio

    // The ratio of the expected video height and the actual browser height
    const inverseHeightRatio = (window.innerWidth * aspectRatio) / window.innerHeight
    // The ratio of the expected video width and the actual browser width
    const inverseWidthRatio = (window.innerHeight / aspectRatio) / window.innerWidth

    const rect = videoWrapper.getBoundingClientRect()
    const translationX = Math.round(
      (window.innerWidth - (videoWidth * scaleX))) - (rect.left / scaleX)
    const translationY = Math.round(
      (window.innerHeight - (videoHeight * scaleY))) - (rect.top / scaleY)

    // A scale value greater than 1 means that it will grow larger than the
    // element's dimensions. Depending on screen size and device orientation
    // either `inverseHeightRatio` or `inverseWidthRatio` will be greater than 1.
    // To get the proper letterboxing effect we need to constrain the larger
    // dimension to the size of the screen and scale the other dimension with
    // the inverse ratio.

    let inverseScale = `scale(1, ${inverseHeightRatio})`

    if (inverseHeightRatio > 1) {
      inverseScale = `scale(${inverseWidthRatio}, 1)`
    }

    return {
      scaleX,
      scaleY,
      translationX,
      translationY,
      topOffset,
      inverseScale
    }
  }

  playFullScreen () {
    const {
      fullscreen,
      fullscreenVideo,
      mainVideo,
      player,
      videoWrapper
    } = this.refs
    const { playInPlace, brandVideo, caseStudyVideo } = this.props

    if (playInPlace) {
      window.scrollTo(0, 0)
      videoWrapper.style.zIndex = 3
      mainVideo.classList.add('hide-after')

      this.setState({
        playOverlay: false
      }, () => {
        player.play()
      })
    } else {
      if (brandVideo || caseStudyVideo) {
        const grandParentElement = videoWrapper.parentElement.parentElement

        if (grandParentElement.previousElementSibling) {
          grandParentElement.previousElementSibling.classList.add('hide')
        }
        grandParentElement.classList.add(`${brandVideo ? 'active' : 'case-study--active'}`)
      }

      mainVideo.style.opacity = 0
      fullscreenVideo.style.opacity = 1

      fullscreen.play()

      if (brandVideo || caseStudyVideo) {
        const {
          scaleX,
          scaleY,
          translationX,
          translationY,
          inverseScale
        } = this.getScalingCoordinates()

        videoWrapper.style.transform = `scale(${scaleX}, ${scaleY}) translate(${
          translationX}px, ${translationY}px)`
        mainVideo.style.transform = inverseScale
        fullscreenVideo.style.transform = inverseScale
        document.documentElement.classList
          .add(`${brandVideo ? 'brand' : 'case-study'}__video-modal--open`)
      } else {
        window.scrollTo(0, 0)
        const { scaleX, scaleY, topOffset, inverseScale } = this.getScalingCoordinates()

        videoWrapper.style.transform = `scale(${scaleX}, ${scaleY}) translateY(-${topOffset}px)`
        mainVideo.style.transform = inverseScale
        fullscreenVideo.style.transform = inverseScale
        document.documentElement.classList.add('video-modal--open')
      }
    }
  }

  closeFullScreen () {
    const {
      fullscreen,
      fullscreenVideo,
      mainVideo,
      player,
      videoWrapper
    } = this.refs
    const { brandVideo, caseStudyVideo } = this.props

    if (brandVideo || caseStudyVideo) {
      const grandParentElement = videoWrapper.parentElement.parentElement

      videoWrapper.addEventListener('transitionend', () => {
        document.documentElement.classList
          .remove(`${brandVideo ? 'brand' : 'case-study'}__video-modal--open`)
        grandParentElement.classList
          .remove(`${brandVideo ? 'active' : 'case-study--active'}`)
        if (grandParentElement.previousElementSibling) {
          grandParentElement.previousElementSibling.classList.remove('hide')
        }
      }, { once: true })
    } else {
      videoWrapper.addEventListener('transitionend', () => {
        document.documentElement.classList.remove('video-modal--open')
        player.play()
      }, { once: true })
    }

    fullscreen.pause()
    fullscreen.load()

    mainVideo.style.opacity = 1
    fullscreenVideo.style.opacity = 0
    videoWrapper.style.transform = ''
    fullscreenVideo.style.transform = ''
    mainVideo.style.transform = ''
  }

  onMouseEnter () {
    if (this.props.hoverPlay) {
      this.refs.player.play()
    }
  }

  onMouseLeave ({ currentTarget: { className } }) {
    if (this.props.hoverPlay && className !== 'works-item__fullscreen') {
      this.refs.player.pause()
    }
  }

  renderPlayerControl () {
    const { playOverlay } = this.state
    const {
      desktopFullVideo,
      mobileFullVideo,
      node,
      externalLink,
      caseButton,
      brandButton,
      cta
    } = this.props

    if (brandButton) {
      return (
        <div className='brand-button' onClick={this.playFullScreen}>
          <i
            id={node.title.trim().replace(/\s+/g, '-')}
            className='fas fa-play'
          />
        </div>
      )
    } else if (externalLink) {
      return (
        <div>
          <OutboundLink
            className='link'
            href={externalLink.url.path} target='_blank'
            rel='noreferrer noopener'
          />
        </div>
      )
    } else if (caseButton) {
      return (
        <div
          id={node.title.trim().replace(/\s+/g, '-')}
          className='works-item__fullscreen'
          onClick={this.playFullScreen}
          onMouseEnter={this.onMouseEnter}
          onMouseLeave={this.onMouseLeave}
        >
          {cta}
        </div>
      )
    } else if ((desktopFullVideo || mobileFullVideo) && playOverlay) {
      return (
        <div>
          <div className='fullscreen' onClick={this.playFullScreen} />
        </div>
      )
    }
  }

  render () {
    const { isMobile, playOverlay } = this.state
    const {
      desktopPosterSrc,
      mobilePosterSrc,
      desktopVideoSrc,
      mobileVideoSrc,
      desktopFullVideo,
      mobileFullVideo,
      aspectRatio,
      autoPlay,
      loop: loopPlay
    } = this.props

    let fullDesktopVideo = ''
    let fullMobileVideo = ''

    if (desktopFullVideo) {
      fullDesktopVideo = desktopFullVideo.file.url
    }
    if (mobileFullVideo) {
      fullMobileVideo = mobileFullVideo.file.url
    }

    const posterShow = isMobile ? mobilePosterSrc : desktopPosterSrc
    const overlaySource = isMobile ? mobileVideoSrc : desktopVideoSrc
    const fullSource = isMobile ? fullMobileVideo : fullDesktopVideo

    return (
      <div className='video--wrapper' ref='videoWrapper'>
        <div
          className='video--main'
          ref='mainVideo'
          onMouseEnter={this.onMouseEnter}
          onMouseLeave={this.onMouseLeave}
        >
          <Player
            ref='player'
            className='video video--hero'
            autoPlay={playOverlay && autoPlay}
            aspectRatio={aspectRatio}
            loop={playOverlay ? loopPlay : false}
            playsInline
            muted={playOverlay}
            poster={posterShow}
            src={playOverlay ? overlaySource : fullSource}
          >
            <BigPlayButton disabled />
            <ControlBar disableCompletely />
            <Shortcut disabled={playOverlay} />
          </Player>
          {this.renderPlayerControl()}
        </div>
        {(desktopFullVideo || mobileFullVideo) &&
          <div className='video--fullscreen' ref='fullscreenVideo'>
            <Player
              ref='fullscreen'
              className='video video--hero'
              autoPlay={false}
              preload='none'
              aspectRatio={aspectRatio}
              loop={false}
              poster={posterShow}
              src={fullSource}
            >
              <BigPlayButton />
              <ControlBar />
            </Player>
            <div className='modal--close' onClick={this.closeFullScreen} />
          </div>
        }
      </div>
    )
  }
}

HeroVideo.defaultProps = {
  autoPlay: true,
  controlBar: false,
  loop: true,
  posterSrc: { desktop: '', mobile: '' }
}

HeroVideo.propTypes = {
  aspectRatio: PropTypes.string,
  autoPlay: PropTypes.bool,
  controlBar: PropTypes.bool,
  loop: PropTypes.bool,
  hoverPlay: PropTypes.bool,
  playInPlace: PropTypes.bool,
  brandVideo: PropTypes.bool,
  caseStudyVideo: PropTypes.bool,
  caseButton: PropTypes.bool,
  externalLink: PropTypes.object,
  cta: PropTypes.string,
  desktopPosterSrc: PropTypes.string,
  mobilePosterSrc: PropTypes.string,
  desktopVideoSrc: PropTypes.string,
  mobileVideoSrc: PropTypes.string,
  desktopFullVideo: PropTypes.object,
  mobileFullVideo: PropTypes.object
}

export default HeroVideo



// WEBPACK FOOTER //
// ./src/components/hero-video.js